<template>
  <div class="w-full flex flex-col gap-2">
    <!-- Label -->
    <div class="flex items-center gap-2" v-if="!dropdownOnly">
      <MetaPlatformIcon v-if="platform === 'meta'" />
      <TikTokPlatformIcon v-if="platform === 'tiktok'" :width="16" :height="16" />
      <BaseText type="label" size="sm" class="text-text-muted">
        Goal Metric
      </BaseText>
    </div>
    <!-- Suggested Metrics -->
    <BigButtonGroup 
      v-if="!dropdownOnly && !disabled"
      :options="suggestedMetrics"
      :selected-value="selectedMetric"
      :render-key="`suggested-metrics-${platform}`"
      option-value-key="key"
      option-label-key="name"
      @change="handleChange"
    />
    <!-- Other Metrics (dropdown) -->
    <div class="relative" v-on-clickaway="() => showGoalMetricDropdown = false">
      <button class="goal-metric-button flex items-center gap-1 w-full px-2 py-1.5 bg-white flex-nowrap whitespace-nowrap rounded-lg focus:outline-none fade-in"
      :class="{'disabled': disabled}"
      @click="showGoalMetricDropdown = true" :disabled="loadingMetrics || disabled">
        <BaseText type="body" size="sm" class="flex-grow text-left truncate mr-0.5"
        :class="selectedMetric?.length ? 'text-text-muted' : 'text-text-normal'">
          {{ selectedMetric?.length ? metricLookup?.[selectedMetric]?.name : 'Select Goal Metric' }}
        </BaseText>
        <BaseLoadingSpinnerCircle v-if="loadingMetrics" class="text-icon-disabled" :duration="1.2" />
        <ChevronIcon v-else-if="!disabled" class="text-icon-normal fade-in" />
      </button>
      <transition>
        <div v-if="showGoalMetricDropdown" class="goal-metric-dropdown">
          <PropertySelectDropdown
            search-placeholder="Search Metrics"
            metrics-only
            emit-keys-only
            use-parent-width
            :excluded-categories="['tags', 'foreplay_metrics']"
            :selected-metrics="selectedMetric?.length ? [selectedMetric] : []"
            :metrics-override="selectorMetrics"
            :platform-override="platform"
            @optionSelected="handleChange"
            @close="showGoalMetricDropdown = false"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'

// Components
import BigButtonGroup from './BigButtonGroup.vue'
import PropertySelectDropdown from '../../filters/PropertySelectDropdown.vue'

// Icons
import MetaPlatformIcon from '../../../globals/Icons/FilterIcons/Platforms/MetaPlatformIcon.vue'
import TikTokPlatformIcon from '../../../globals/Icons/FilterIcons/Platforms/TikTokPlatformIcon.vue'
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'

export default {
  name: 'GoalMetricSelector',
  mixins: [clickaway],
  components: {
    BigButtonGroup,
    PropertySelectDropdown,
    MetaPlatformIcon,
    TikTokPlatformIcon,
    ChevronIcon
  },
  props: {
    platform: {
      type: String,
      required: true
    },
    metricsOverride: {
      type: Object,
      default: () => ({})
    },
    selectedMetric: {
      required: true
    },
    loadingMetrics: {
      type: Boolean,
      default: false
    },
    dropdownOnly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showGoalMetricDropdown: false
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricLookup']),
    selectorMetrics () {
      return _.isEmpty(this.metricsOverride) ? {} : this.metricsOverride
    },
    metricLookup () {
      return this.metricsOverride?.metricLookup ?? this.getMetricLookup
    },
    suggestedMetrics () {
      return [
        { name: 'ROAS', key: 'roas' },
        { name: 'CPA', key: 'cpa' },
        { name: 'CTR', key: 'ctr' }
      ]
    }
  },
  methods: {
    handleChange (metricKey) {
      this.$emit('changeMetric', metricKey)
      this.showGoalMetricDropdown = false
    }
  }
}
</script>

<style scoped>
.goal-metric-button {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out;
  border-radius: 8px;
}
.goal-metric-button:hover:not(.disabled) {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.12), 0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}
.goal-metric-button.disabled {
  cursor: default;
}
.goal-metric-dropdown {
  position: absolute;
  top: -4px;
  left: -2px;
  right: -2px;
  z-index: 500;
}

.v-enter-active, .v-leave-active {
  transition: opacity 100ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.fade-in {
  animation: fadeIn 100ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>